import React from 'react'
import { useAppSelector } from '@SonarCX/hooks/redux'

import { APITaskData } from '@SonarCX/components/molecules/TaskWidget'

import { sortByDueDateAndPriority } from '../../BodyWidgets/TaskRow/helpers'
import { selectTaskPriority } from '@SonarCX/slices/priority'
import { useEnvironment } from '@SonarCX/providers/EnvironmentProvider'
import { useAuth } from '@SonarCX/providers/AuthProvider'

type UseDashboardTasksOptions = {
  mineOnly: boolean
  dateRangeStart: Date
  dateRangeEnd: Date
}

const useDashboardTasks = ({
  mineOnly,
  dateRangeStart,
  dateRangeEnd
}: UseDashboardTasksOptions) => {
  const { priorities } = useAppSelector(selectTaskPriority)

  const { user, loaded: envLoaded } = useEnvironment()
  const { isLoggedIn } = useAuth()

  const userID = React.useMemo(() => user?.id, [user])
  const canLoad = React.useMemo(
    () => isLoggedIn && envLoaded && Boolean(userID),
    [isLoggedIn, envLoaded, userID]
  )

  const getKey = React.useCallback(
    (pageIndex: number, previousPageData: APITaskData) => {
      if (!canLoad) {
        return null
      }

      // Reached the end
      if (previousPageData && !previousPageData.data) {
        return null
      }

      // First page, we don't have `previousPageData`
      let pageNum = 0

      if (pageIndex !== 0) {
        // Add the cursor to the API endpoint
        pageNum = pageIndex + 1
      }

      // Encode the date range into the key as a string
      const dateRange = `${dateRangeStart
        .toISOString()
        .replaceAll(':', '..')}--${dateRangeEnd
        .toISOString()
        .replaceAll(':', '..')}`

      const id = mineOnly ? userID : 'null'

      return `taskDashboard2:${pageNum}:${id}:${dateRange}`
    },
    [canLoad, mineOnly, userID, dateRangeStart, dateRangeEnd]
  )

  const formatTasks = React.useCallback(
    (tasks: APITask[]) => {
      const sorted = sortByDueDateAndPriority(tasks, priorities)
      return sorted
    },
    [priorities]
  )

  return {
    getKey,
    formatTasks
  }
}

export default useDashboardTasks
